import type { ConstraintInputType } from '../../../../config/optimizePlanConfig';
import type { MetricFormat } from '../../../../config/metrics';

// Derived from ConstraintLevelOption['key'] — stays in sync with the config.
export type Level = string;

/**
 * One selectable KPI in the Select Constraints popover.
 *
 * Comes from the `get_optimization_original_vals` response, not from config: that
 * one call returns every constraint at every level, already filtered to the ones
 * this objective can be constrained on. `key` is sent back verbatim as
 * `range_constraints[].constraint_name`.
 */
export interface ConstraintCatalogEntry {
    key: string;
    label: string;
    /** `range` renders Min/Max inputs; `number` renders a single input. */
    inputType: ConstraintInputType;
    format: MetricFormat;
}

export interface FormData {
    objective: string | null;
    optimization_start_date: number | null;
    keep_top: number | null;
    concurrent_tprs: number | null;
    concurrent_fds: number | null;
    concurrent_mdfs: number | null;
    [key: string]: string | number | null;
}

// -----------------------------------------------------------------------------
// Selection state — the user's explicit intent, and the only constraint state the
// modal stores. Everything the table renders is derived from these plus the
// fetched baselines, so a refetch can never disagree with what is on screen.
// -----------------------------------------------------------------------------

export interface ConstraintLevelSelection {
    level: Level;
    /**
     * Attribute names the user deleted from this level block. Kept as an exclusion
     * list rather than a materialised row list so a refetch after a scope change
     * still yields the current attribute set.
     */
    removedAttributes: string[];
}

export interface ConstraintSelection {
    /** A `constraint_details[].name` from the original-values response. */
    name: string;
    levels: ConstraintLevelSelection[];
}

/** One editable cell's value. `single` is used when the KPI's inputType is `number`. */
export interface ConstraintInputValue {
    min: number | null;
    max: number | null;
    single: number | null;
}

/** Identifies one attribute row across the whole tab. */
export interface ConstraintRowRef {
    constraintName: string;
    level: Level;
    attributeName: string;
}

// -----------------------------------------------------------------------------
// Derived render model — rebuilt every render from selections + query results.
// -----------------------------------------------------------------------------

export type ConstraintLevelStatus = 'loading' | 'error' | 'empty' | 'ready';

export interface ConstraintAttributeRow {
    /** Backend attribute value, e.g. `"Brand 1"`. Row identity and display label. */
    attributeName: string;
    /** Read-only column values keyed by config column key. */
    fields: Record<string, unknown>;
    input: ConstraintInputValue;
    /** True when the input is incomplete or inverted, so the footer can block the run. */
    hasInvalidInput: boolean;
}

export interface ConstraintLevelBlock {
    level: Level;
    levelLabel: string;
    status: ConstraintLevelStatus;
    rows: ConstraintAttributeRow[];
}

export interface ConstraintGroup {
    name: string;
    label: string;
    inputType: ConstraintInputType;
    format: MetricFormat;
    levels: ConstraintLevelBlock[];
    /** Level options still addable to this KPI, with the scope floor already applied. */
    addableLevels: { key: Level; label: string; disabled: boolean }[];
}

export const INITIAL_FORM_DATA: FormData = {
    optimization_start_date: null,
    objective: null,
    keep_top: null,
    concurrent_tprs: null,
    concurrent_fds: null,
    concurrent_mdfs: null,
};
